import * as React from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "motion/react"
import {
  X,
  Clock,
  Users,
  Sparkles,
  Trophy,
  Zap,
  CalendarDays,
  Crown,
  Loader2,
  ImageIcon,
} from "lucide-react"
import { useAuth, type GameEventWithProgress } from "@/contexts/AuthContext"
import { getEventIcon } from "@/lib/iconMap"

// Countdown hook
function useCountdown(targetDate: string | null | undefined) {
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })

  React.useEffect(() => {
    if (!targetDate) return
    const target = new Date(targetDate).getTime()
    if (!Number.isFinite(target)) return

    const tick = () => {
      const now = Date.now()
      const diff = Math.max(0, target - now)
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      })
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [targetDate])

  return timeLeft
}

function CountdownDigit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <motion.div
        className="w-10 h-12 bg-[#1A1A30] rounded-xl border-2 border-[#2A2A45] flex items-center justify-center shadow-[0_0_8px_rgba(155,89,182,0.15)]"
        key={value}
        initial={{ rotateX: -30, opacity: 0.5 }}
        animate={{ rotateX: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <span
          className="text-xl font-black text-white tabular-nums"
          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          {String(value).padStart(2, "0")}
        </span>
      </motion.div>
      <span
        className="text-[8px] font-bold text-[#6A6A8A] uppercase tracking-wider"
        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
      >
        {label}
      </span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; border: string; label: string; pulse?: boolean }> = {
    coming_soon: { bg: "bg-[#FFD700]/15", text: "text-[#FFD700]", border: "border-[#FFD700]/30", label: "Coming Soon" },
    live: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30", label: "LIVE", pulse: true },
    ended: { bg: "bg-white/5", text: "text-white/40", border: "border-white/10", label: "Ended" },
    draft: { bg: "bg-white/5", text: "text-white/30", border: "border-white/10", label: "Draft" },
  }
  const c = config[status] || config.draft!

  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg border ${c.bg} ${c.text} ${c.border}`}>
      {c.pulse && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />}
      {c.label}
    </span>
  )
}

interface GameEventDetailModalProps {
  open: boolean
  onClose: () => void
  eventId: string | null
}

export function GameEventDetailModal({ open, onClose, eventId }: GameEventDetailModalProps) {
  const { getGameEventDetail } = useAuth()
  const [event, setEvent] = React.useState<GameEventWithProgress | null>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!open || !eventId) return
    setLoading(true)
    getGameEventDetail(eventId).then((result) => {
      if (result.success && result.event) {
        setEvent(result.event)
      }
      setLoading(false)
    })
  }, [open, eventId, getGameEventDetail])

  React.useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [open, onClose])

  const countdown = useCountdown(event?.status === "coming_soon" ? event.startDate : event?.endDate)
  const isCountingDown = event?.status === "coming_soon" || event?.status === "live"
  const countdownLabel = event?.status === "coming_soon" ? "Starts In" : "Ends In"

  const EventIcon = event ? getEventIcon(event.iconName) : Trophy

  const modalContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="relative w-full max-w-md max-h-[85vh] bg-[#161628] rounded-3xl border-[3px] border-[#2A2A45] shadow-[0_0_40px_rgba(243,112,32,0.15)] overflow-hidden flex flex-col"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", bounce: 0.25, duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-8 h-8 rounded-xl bg-[#1C1C32] border-2 border-[#2A2A45] flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-4 h-4 text-white/60" />
            </motion.button>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <motion.div
                    className="w-12 h-12 rounded-full border-3 border-[#F37020] border-t-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <span className="text-sm text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                    Loading event...
                  </span>
                </div>
              ) : event ? (
                <div className="flex flex-col">
                  {/* Banner Image */}
                  {(event.detailImageUrl || event.imageUrl) && (
                    <div className="relative w-full aspect-[16/9] bg-[#1A1A30] overflow-hidden">
                      <img
                        src={event.detailImageUrl || event.imageUrl || ""}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#161628] via-transparent to-transparent" />
                    </div>
                  )}

                  <div className="p-5 flex flex-col gap-4">
                    {/* Header */}
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <StatusBadge status={event.status} />
                        <span
                          className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border border-[#2A2A45]/60 text-white"
                          style={{ backgroundColor: event.badgeColor }}
                        >
                          {event.badge}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center border-2 border-[#2A2A45] flex-shrink-0"
                          style={{ backgroundColor: `${event.badgeColor}20` }}
                        >
                          <EventIcon className="w-5 h-5" style={{ color: event.badgeColor }} />
                        </div>
                        <h2
                          className="text-xl font-black text-white leading-tight"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          {event.title}
                        </h2>
                      </div>
                    </div>

                    {/* Description */}
                    {event.description && (
                      <p
                        className="text-sm text-[#9A9ABF] leading-relaxed"
                        style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                      >
                        {event.description}
                      </p>
                    )}

                    {/* Points Multiplier */}
                    {event.pointsMultiplier && event.pointsMultiplier > 1 && (
                      <motion.div
                        className="bg-gradient-to-r from-[#F37020]/15 to-[#FFD700]/15 rounded-2xl border-2 border-[#F37020]/30 p-3.5 text-center"
                        animate={{ boxShadow: ["0 0 15px rgba(243,112,32,0)", "0 0 25px rgba(243,112,32,0.15)", "0 0 15px rgba(243,112,32,0)"] }}
                        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Zap className="w-5 h-5 text-[#FFD700]" />
                          <span
                            className="text-2xl font-black text-[#FFD700]"
                            style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                          >
                            {event.pointsMultiplier}x Points
                          </span>
                          <Zap className="w-5 h-5 text-[#FFD700]" />
                        </div>
                        <p className="text-[11px] text-[#F37020] font-bold mt-1" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                          All deals earn {event.pointsMultiplier}x points during this event!
                        </p>
                      </motion.div>
                    )}

                    {/* Countdown */}
                    {isCountingDown && (event.startDate || event.endDate) && (
                      <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5">
                        <p
                          className="text-[10px] text-[#9A9ABF] font-bold mb-2.5 uppercase tracking-wider text-center"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          {countdownLabel}
                        </p>
                        <div className="flex items-center justify-center gap-1.5">
                          <CountdownDigit value={countdown.days} label="Days" />
                          <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                          <CountdownDigit value={countdown.hours} label="Hrs" />
                          <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                          <CountdownDigit value={countdown.minutes} label="Min" />
                          <span className="text-lg font-black text-[#6A6A8A] mt-[-12px]">:</span>
                          <CountdownDigit value={countdown.seconds} label="Sec" />
                        </div>
                      </div>
                    )}

                    {/* Progress Section */}
                    {event.status === "live" && event.progress && event.progress.requirementProgress.length > 0 && (
                      <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5">
                        <p
                          className="text-[10px] text-[#9A9ABF] font-bold mb-3 uppercase tracking-wider"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          Your Progress
                        </p>
                        <div className="space-y-3">
                          {event.progress.requirementProgress.map((req, i) => {
                            const actionReq = event.actionRequirements[req.requirementIndex]
                            const progress = Math.min(req.current / req.target, 1)
                            return (
                              <div key={i}>
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-xs text-white/70 font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                                    {actionReq?.metric || `Requirement ${i + 1}`}
                                  </span>
                                  <span className="text-xs font-black" style={{ color: req.completed ? "#32CD32" : event.badgeColor, fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                                    {req.current}/{req.target}
                                  </span>
                                </div>
                                <div className="w-full h-3 bg-[#1A1A30] rounded-full border border-[#2A2A45]/50 overflow-hidden">
                                  <motion.div
                                    className="h-full rounded-full"
                                    style={{
                                      background: req.completed
                                        ? "linear-gradient(90deg, #32CD32, #7FFF00)"
                                        : `linear-gradient(90deg, ${event.badgeColor}, #FFD700)`,
                                    }}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress * 100}%` }}
                                    transition={{ duration: 0.8, ease: "easeOut" }}
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        {event.progress.status === "completed" && (
                          <motion.div
                            className="mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[#32CD32]/15 border border-[#32CD32]/40"
                            animate={{ scale: [1, 1.02, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <Sparkles className="w-4 h-4 text-[#32CD32]" />
                            <span className="text-sm font-bold text-[#32CD32]" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                              Event Completed!
                            </span>
                          </motion.div>
                        )}
                      </div>
                    )}

                    {/* Action Requirements (no progress yet) */}
                    {event.actionRequirements.length > 0 && !event.progress && event.status !== "ended" && (
                      <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5">
                        <p
                          className="text-[10px] text-[#9A9ABF] font-bold mb-2.5 uppercase tracking-wider"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          Requirements
                        </p>
                        <div className="space-y-2">
                          {event.actionRequirements.map((req, i) => (
                            <div key={i} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-[#1A1A30]/50">
                              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${event.badgeColor}20` }}>
                                <Trophy className="w-3.5 h-3.5" style={{ color: event.badgeColor }} />
                              </div>
                              <span className="text-xs font-bold text-white/70" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                                {req.metric}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rewards */}
                    {event.rewards.length > 0 && (
                      <div className="bg-[#1C1C32] rounded-2xl border-2 border-[#2A2A45] p-3.5">
                        <p
                          className="text-[10px] text-[#9A9ABF] font-bold mb-2.5 uppercase tracking-wider"
                          style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                        >
                          Rewards
                        </p>
                        <div className="space-y-2">
                          {event.rewards.map((reward, i) => (
                            <motion.div
                              key={i}
                              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#1A1A30] border border-[#2A2A45]/50"
                              initial={{ opacity: 0, x: 8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.05 }}
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#FFD700]/15 border border-[#FFD700]/30 flex items-center justify-center">
                                {reward.type === "cash" ? (
                                  <Crown className="w-4 h-4 text-[#FFD700]" />
                                ) : reward.type === "points" ? (
                                  <Sparkles className="w-4 h-4 text-[#9B59B6]" />
                                ) : (
                                  <Trophy className="w-4 h-4 text-[#F37020]" />
                                )}
                              </div>
                              <span
                                className="text-sm font-bold text-white"
                                style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}
                              >
                                {reward.label}
                              </span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-3 text-[11px] text-[#6A6A8A] font-bold" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                      {event.participantCount > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" />
                          {event.participantCount} participant{event.participantCount !== 1 ? "s" : ""}
                        </span>
                      )}
                      {event.startDate && (
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3.5 h-3.5" />
                          {new Date(event.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          {event.endDate && ` – ${new Date(event.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <ImageIcon className="w-8 h-8 text-white/20" />
                  <span className="text-sm text-[#6A6A8A] font-medium" style={{ fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                    Event not found
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  if (typeof document === "undefined") return null
  return createPortal(modalContent, document.body)
}
